/**
 * @file FadeRise — screen-entrance animation wrapper
 * @version 0.1.0
 */

import React, { type ElementType, type HTMLAttributes, type ReactNode } from 'react';

/*
 * FadeRise — screen-entrance wrapper.
 *
 * Pure presentational wrapper: applies the brand's rise-fade entrance class
 * (and an optional stagger-delay class) to its child. No animation logic lives
 * here — the keyframes, durations, easings, and the prefers-reduced-motion
 * guard are all defined in the brand stylesheet (motion.css, loaded via the
 * styles entry point). Mount-triggered: the rise runs when the element first
 * renders.
 *
 *   <FadeRise>…</FadeRise>                 single block, immediate rise
 *   <FadeRise step={2}>…</FadeRise>        staggered: waits its turn in the cascade
 *   <FadeRise as="section" step={1}>…</FadeRise>   rise applied to a semantic element
 *
 * Requires the brand stylesheet (which defines .hd-rise / .hd-rise-d1..d8) to
 * be loaded by the consumer.
 */

export type FadeRiseStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface FadeRiseProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /**
   * Stagger position 1–8. Applies the matching delay so a group of siblings
   * composes in sequence rather than all at once. Omit for an immediate rise.
   */
  step?: FadeRiseStep;
  /**
   * Element type to render. Default 'div'. Set to apply the entrance directly
   * to a semantic element (e.g. 'section', 'h1') instead of a wrapper div.
   */
  as?: ElementType;
  className?: string;
}

export function FadeRise({
  children,
  step,
  as: Component = 'div',
  className,
  ...rest
}: FadeRiseProps) {
  const classes = ['hd-rise', step != null ? `hd-rise-d${step}` : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
